import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfmPlugin from 'remark-gfm';
import cx from 'classnames';
import _ from 'lodash';
import { safeParseInt } from '../../../common/utils';
import { Link, TrackedLink } from '../link';
import { withSiteMeta } from '../../HOCS/withSiteMeta';
import { checkIfRouteExists } from '../../libs/history';

const VALID_SCHEMES = ['http://', 'https://'];

// domains that indicate the running webserver
const INTERNAL_DOMAINS = ['raider.io', 'classic.raider.io', 'era.raider.io'];

// all domains we want to allow autolinking
const VALID_AUTOLINK_DOMAINS = [
  ...INTERNAL_DOMAINS,
  'rio.gg',
  'keystone.guru',

  // data/guides
  'wowhead.com',
  'icy-veins.com',

  // social media/streaming
  'discord.gg',
  'twitch.tv',
  'www.twitch.tv',
  'youtube.com',
  'www.youtube.com',
  'twitter.com',
  'x.com',

  // internet resources
  'forms.gle',
  'tiltify.com',

  // blizzard stuff
  'blizzard.com/invite/', // for wow communities
  'worldofwarcraft.com/invite/', // for wow communities
  'www.worldofwarcraft.com/invite/', // for wow communities
  'worldofwarcraft.blizzard.com',
  'wow.blizzard.cn',

  // liquid partnership
  'liquidarmory.com',
  'teamliquid.com',
  'tl.gg'
];

function imageUriTransformer(uri: string): string | undefined {
  const url = (uri || '').trim();
  if (url.charAt(0) === '/') {
    return url;
  }

  const BASE_URL = RIO_SITE.cdn_url;
  if (url.indexOf(BASE_URL) === 0) {
    return url.slice(BASE_URL.length);
  }

  if (url.indexOf('=') !== -1) {
    // # indicates we need to process it to get the specifics
    return `#${url}`;
  }

  return undefined;
}

function linkUriTransformer(uri: string): string | undefined {
  const baseUrl = (uri || '').trim();
  if (baseUrl.charAt(0) === '/') {
    return baseUrl;
  }

  let url = baseUrl;
  for (const scheme of VALID_SCHEMES) {
    if (url.indexOf(scheme) === 0) {
      url = url.slice(scheme.length);

      for (const domain of VALID_AUTOLINK_DOMAINS) {
        if (url.indexOf(domain) === 0) {
          if (INTERNAL_DOMAINS.includes(domain)) {
            return url.slice(domain.length) || '/';
          }

          // non-raider.io url
          return baseUrl;
        }
      }
    }
  }

  return undefined;
}

// Combined URL transformer for react-markdown v10
// key is 'src' for images, 'href' for links
function createUrlTransformer(allowImages: boolean, allowAllLinks: boolean) {
  return (url: string, key: string, _node: import('hast').Element): string | undefined => {
    if (key === 'src') {
      // Image URL transformation
      return allowImages ? imageUriTransformer(url) : undefined;
    }
    // Link URL transformation
    return allowAllLinks ? url : linkUriTransformer(url);
  };
}

type TMarkdownRendererProps = {
  markdown: string;
  allowImages: boolean;
  allowAllImages?: boolean;
  allowAllLinks: boolean;
};

function removeScheme(url: string) {
  if (typeof url === 'string') {
    for (const scheme of VALID_SCHEMES) {
      if (url.indexOf(scheme) === 0) {
        return url.slice(scheme.length);
      }
    }
  }

  return url;
}

function getMarkdownComponents(params: any) {
  const dungeonsBySlug = _.groupBy(params.siteMeta.dungeons, 'slug');
  const raidsBySlug = _.groupBy(params.siteMeta.raids, 'slug');
  const raidPartitionsBySlug = _.keyBy(
    params.siteMeta.raids.flatMap((r: any) => r.partitions ?? []),
    'slug'
  );
  const specsBySlug = _.groupBy(params.siteMeta.specs, 'slug');
  const classesBySlug = _.groupBy(params.siteMeta.classes, 'slug');
  const affixesBySlug = _.groupBy(params.siteMeta.affixes, 'slug');
  const factionsBySlug: { [key: string]: string } = {
    alliance: '/images/site/alliance_icon4.png',
    horde: '/images/site/horde_icon4.png'
  };

  const avatarSizes: { [key: string]: string } = {
    ['x-small']: 'x-small',
    small: 'small',
    medium: 'medium',
    large: 'large'
  };

  return {
    a(props: any) {
      if (props.href) {
        try {
          decodeURI(props.href);
        } catch (err) {
          return '[invalid-url]';
        }

        // Get text content properly - children might be string, array, or React elements
        const getTextContent = () => {
          if (typeof props.children === 'string') {
            return removeScheme(props.children);
          }
          if (Array.isArray(props.children) && typeof props.children[0] === 'string') {
            return removeScheme(props.children[0]);
          }
          return props.children;
        };

        const textContent = getTextContent();

        if (props.href.charAt(0) === '/') {
          const doesRouteExist = checkIfRouteExists(props.href);

          if (!doesRouteExist) {
            return <a href={props.href}>{textContent}</a>;
          }

          return <Link to={props.href}>{textContent}</Link>;
        }

        return (
          <TrackedLink rel="noopener nofollow" href={props.href} target="_blank">
            {textContent}
          </TrackedLink>
        );
      }

      return <>{props.children}</>;
    },

    img(props: any) {
      if (!props.src || !params.allowImages) {
        return null;
      }

      if (props.src.charAt(0) === '#') {
        // process special image shortcuts
        const shortcutItems = props.src.slice(1).split(/,/);
        const shortcutParts = shortcutItems.reduce((accum: any, item: any) => {
          const kvs = item.split('=');
          accum[kvs[0]] = kvs[1];
          return accum;
        }, {});

        let path = '';
        let isIcon = true;
        let isVideo = false;
        let alignCenter = false;
        let showBorder = true;

        if (shortcutParts.image) {
          if (!params.allowAllImages) {
            return null;
          }

          path = shortcutParts.image;
          showBorder = safeParseInt(shortcutParts.border, 1) !== 0;
          alignCenter = safeParseInt(shortcutParts.center, 1) !== 0;
          isIcon = false;
        } else if (shortcutParts.icon) {
          path = `/images/wow/icons/large/${shortcutParts.icon}.jpg`;
        } else if (shortcutParts.spec && shortcutParts.class) {
          if (specsBySlug[shortcutParts.spec] && classesBySlug[shortcutParts.class]) {
            path = `/images/classes/spec_${shortcutParts.class}_${shortcutParts.spec}.png`;
          }
        } else if (shortcutParts.class) {
          if (classesBySlug[shortcutParts.class]) {
            path = `/images/classes/class_${shortcutParts.class}.png`;
          }
        } else if (shortcutParts.raid) {
          if (raidsBySlug[shortcutParts.raid]) {
            path = `/images/${shortcutParts.raid}/icon.jpg`;
          }
        } else if (shortcutParts.raidheader) {
          if (raidsBySlug[shortcutParts.raidheader]) {
            isIcon = false;
            path = `/images/${shortcutParts.raidheader}/headers/_zone.jpg`;
          } else {
            const partition = raidPartitionsBySlug[shortcutParts.raidheader];
            if (partition) {
              isIcon = false;
              path = `/images/${partition.parentSlug}/headers/partitions/${partition.slug}.jpg`;
            }
          }
        } else if (shortcutParts.affix) {
          const affix = affixesBySlug[shortcutParts.affix]?.[0];
          if (affix) {
            path = `/images/wow/icons/large/${affix.icon}.jpg`;
          }
        } else if (shortcutParts.dungeon) {
          const dungeon = dungeonsBySlug[shortcutParts.dungeon]?.[0];
          if (dungeon) {
            path = `/images/keystone-icons/${dungeon.id}.jpg`;
          }
        } else if (shortcutParts.dungeonheader) {
          const dungeon = dungeonsBySlug[shortcutParts.dungeonheader]?.[0];
          if (dungeon) {
            isIcon = false;
            path = `/images/dungeons/expansion${dungeon.expansion_id}/base/${dungeon.slug}.jpg`;
          }
        } else if (shortcutParts.faction) {
          const factionPath = factionsBySlug[shortcutParts.faction];
          if (factionPath) {
            path = factionPath;
          }
        } else if (shortcutParts.youtube) {
          isVideo = true;
          path = shortcutParts.youtube;
        }

        if (!path) {
          return null;
        }

        if (isVideo) {
          const params = [`origin=${encodeURIComponent(RIO_SITE.external_hostname)}`];
          if (shortcutParts.time) {
            params.push(`start=${shortcutParts.time}`);
          }
          const embedUrl = `https://www.youtube-nocookie.com/embed/${path}?${params.join('&')}`;
          return (
            <div className="rio-embed--youtube slds-text-align--center">
              <iframe
                className="rio-border--light"
                src={embedUrl}
                frameBorder={0}
                scrolling="0"
                allowFullScreen={true}
              />
            </div>
          );
        } else if (isIcon) {
          return (
            <span
              className={`rio-border--light rio-shadow--small slds-avatar slds-avatar--${
                avatarSizes[shortcutParts.size] || 'small'
              }`}
            >
              <img src={`${RIO_SITE.cdn_url}${path}`} />
            </span>
          );
        } else {
          const imgComponent = (
            <img className={showBorder ? 'rio-border--light' : ''} src={`${RIO_SITE.cdn_url}${path}`} />
          );

          if (alignCenter) {
            return <div className="slds-text-align--center">{imgComponent}</div>;
          }

          return imgComponent;
        }
      }

      return <img src={`${RIO_SITE.cdn_url}${props.src}`} />;
    },

    ul(props: any) {
      return <ul className={cx(props.className, 'slds-list slds-list--vertical')}>{props.children}</ul>;
    },

    li(props: any) {
      return <li className={cx(props.className, 'slds-item')}>{props.children}</li>;
    },

    hr(props: any) {
      return <hr className="slds-m-vertical--small" />;
    },

    h1(props: any) {
      return (
        <div className={cx(props.className, 'text-bold slds-m-vertical--small')} style={{ fontSize: '1.25rem' }}>
          {props.children}
        </div>
      );
    },

    h2(props: any) {
      return (
        <div className={cx(props.className, 'text-bold slds-m-vertical--small')} style={{ fontSize: '1.125rem' }}>
          {props.children}
        </div>
      );
    },

    h3(props: any) {
      return (
        <div className={cx(props.className, 'text-bold slds-m-vertical--small')} style={{ fontSize: '1rem' }}>
          {props.children}
        </div>
      );
    },

    h4(props: any) {
      return (
        <div className={cx(props.className, 'text-bold slds-m-vertical--small')} style={{ fontSize: '0.875rem' }}>
          {props.children}
        </div>
      );
    },

    h5(props: any) {
      return (
        <div className={cx(props.className, 'text-bold slds-m-vertical--small')} style={{ fontSize: '0.75rem' }}>
          {props.children}
        </div>
      );
    },

    h6(props: any) {
      return (
        <div className={cx(props.className, 'text-bold slds-m-vertical--small')} style={{ fontSize: '0.625rem' }}>
          {props.children}
        </div>
      );
    },

    input(props: any) {
      return (
        <span className={cx(props.className, 'slds-form-element__control slds-m-right--xx-small')}>
          <input className="slds-form-element" type="checkbox" checked={props.checked} />
          <label className="slds-checkbox__label" htmlFor="is_hidden">
            <span className="slds-checkbox--faux" />
          </label>
        </span>
      );
    }
  };
}

const MarkdownRenderer = withSiteMeta((props: TMarkdownRendererProps) => {
  const markdownComponents = useMemo(() => getMarkdownComponents(props), []);
  const urlTransform = useMemo(
    () => createUrlTransformer(props.allowImages, props.allowAllLinks),
    [props.allowImages, props.allowAllLinks]
  );

  // make it so newline behavior is more intuitive for users who do not
  // know markdown, which will be most of our users
  // Convert single newlines to <br> by adding two spaces before them (markdown line break syntax)
  // Then handle multiple consecutive newlines for paragraph spacing
  const revisedMarkdown = props.markdown
    .replace(/([^\n])\n([^\n])/g, '$1  \n$2') // single newlines become line breaks (two spaces + newline)
    .replace(/\n(\n+)/g, (match: string, p1: string) => {
      return '\n\n' + _.repeat('&nbsp;\n\n', p1.length - 1);
    });

  return (
    <ReactMarkdown remarkPlugins={[remarkGfmPlugin]} urlTransform={urlTransform} components={markdownComponents}>
      {revisedMarkdown}
    </ReactMarkdown>
  );
});

export default MarkdownRenderer;
